/* eslint-disable max-lines */
import { ChevronDownIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../../../hooks/useUser";
import { LineSelectList } from "../../../interfaces/endpoints/lines/ILines";
import { BulkUpdateLocationState, TripPreviewRow } from "../../../interfaces/endpoints/trips/IBulkEditTripsWorkingDays";
import { ITripType } from "../../../interfaces/endpoints/trips/Trips.types";
import { getLinesSelectList } from "../../../services/Lines.service";
import { getTripsByLine } from "../../../services/Trips.service";
import Button from "../../ui/Button";
import SpinnerLoader from "../../ui/SpinnerLoader";
import Select from "../../ui/select/Select";

interface BulkLineTripPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Track selected line and its trips
interface SelectedLineState {
  line: LineSelectList;
  trips: ITripType[];
  tripsLoaded: boolean;
  tripsLoading: boolean;
  // Set of trip IDs that are deselected (by default all are selected)
  deselectedTripIds: Set<number>;
  expanded: boolean;
}

const BulkLineTripPickerModal: React.FC<BulkLineTripPickerModalProps> = ({
  isOpen,
  onClose
}) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [selectedLines, setSelectedLines] = useState<Map<number, SelectedLineState>>(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedLines(new Map());
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Handle line selection/deselection from multi-select
  const handleLinesChange = useCallback((lines: LineSelectList[], changedLine?: LineSelectList) => {
    if (!changedLine) return;

    setSelectedLines((prev) => {
      const newMap = new Map(prev);
      const lineId = changedLine.value;

      if (newMap.has(lineId)) {
        // Line was deselected - remove it
        newMap.delete(lineId);
      } else {
        // Line was selected - add it with default state
        newMap.set(lineId, {
          line: changedLine,
          trips: [],
          tripsLoaded: false,
          tripsLoading: false,
          deselectedTripIds: new Set(),
          expanded: false
        });
      }

      return newMap;
    });
  }, []);

  // Toggle line expansion and load trips if not loaded
  const toggleLineExpansion = useCallback(async (lineId: number) => {
    const lineState = selectedLines.get(lineId);
    if (!lineState) return;

    // If not expanded and trips not loaded, load them
    if (!lineState.expanded && !lineState.tripsLoaded) {
      setSelectedLines((prev) => {
        const newMap = new Map(prev);
        const state = newMap.get(lineId);
        if (state) {
          newMap.set(lineId, { ...state, tripsLoading: true });
        }
        return newMap;
      });

      try {
        const response = await getTripsByLine(lineId);
        const trips = response.data || [];

        setSelectedLines((prev) => {
          const newMap = new Map(prev);
          const state = newMap.get(lineId);
          if (state) {
            newMap.set(lineId, {
              ...state,
              trips,
              tripsLoaded: true,
              tripsLoading: false,
              expanded: true
            });
          }
          return newMap;
        });
      } catch (error) {
        console.error("Failed to load trips for line", lineId, error);
        setSelectedLines((prev) => {
          const newMap = new Map(prev);
          const state = newMap.get(lineId);
          if (state) {
            newMap.set(lineId, { ...state, tripsLoading: false });
          }
          return newMap;
        });
      }
    } else {
      // Just toggle expansion
      setSelectedLines((prev) => {
        const newMap = new Map(prev);
        const state = newMap.get(lineId);
        if (state) {
          newMap.set(lineId, { ...state, expanded: !state.expanded });
        }
        return newMap;
      });
    }
  }, [selectedLines]);

  // Toggle a specific trip selection
  const toggleTripSelection = useCallback((lineId: number, tripId: number) => {
    setSelectedLines((prev) => {
      const newMap = new Map(prev);
      const state = newMap.get(lineId);
      if (!state) return prev;

      const newDeselected = new Set(state.deselectedTripIds);
      if (newDeselected.has(tripId)) {
        newDeselected.delete(tripId);
      } else {
        newDeselected.add(tripId);
      }

      // If all trips are now deselected, remove the line entirely
      if (state.tripsLoaded && newDeselected.size === state.trips.length) {
        newMap.delete(lineId);
      } else {
        newMap.set(lineId, { ...state, deselectedTripIds: newDeselected });
      }

      return newMap;
    });
  }, []);

  // Remove a line entirely
  const removeLine = useCallback((lineId: number) => {
    setSelectedLines((prev) => {
      const newMap = new Map(prev);
      newMap.delete(lineId);
      return newMap;
    });
  }, []);

  // Compute selected lines as array for the Select component
  const selectedLinesArray = Array.from(selectedLines.values()).map((s) => s.line);

  // Submit selection and navigate to bulk update page
  const handleSubmit = useCallback(async () => {
    if (selectedLines.size === 0) return;

    setIsSubmitting(true);

    try {
      // Load trips for any line that hasn't been expanded yet
      const tripIds: number[] = [];
      const tripPreviewRows: TripPreviewRow[] = [];

      const loadPromises: Promise<void>[] = [];

      selectedLines.forEach((state, lineId) => {
        if (state.tripsLoaded) {
          // Add already loaded trips (excluding deselected)
          state.trips.forEach((trip) => {
            if (!state.deselectedTripIds.has(trip.trip.id)) {
              tripIds.push(trip.trip.id);
              const hasCustomDates = !!(trip.customWorkingDays && trip.customWorkingDays.length > 0);
              tripPreviewRows.push({
                tripId: trip.trip.id,
                routeName: trip.routeName,
                departureTimeString: trip.trip.departureTimeString,
                arrivalTimeString: trip.trip.arrivalTimeString,
                operationalInterval: trip.operationalInterval,
                customWorkingDays: trip.customWorkingDays,
                hasCustomDates,
                customDatesType: hasCustomDates ? trip.customWorkingDays![0].type : undefined,
                prices: trip.prices
              });
            }
          });
        } else {
          // Need to load trips for this line
          const loadPromise = getTripsByLine(lineId).then((response) => {
            const trips = response.data || [];
            trips.forEach((trip) => {
              if (!state.deselectedTripIds.has(trip.trip.id)) {
                tripIds.push(trip.trip.id);
                const hasCustomDates = !!(trip.customWorkingDays && trip.customWorkingDays.length > 0);
                tripPreviewRows.push({
                  tripId: trip.trip.id,
                  routeName: trip.routeName,
                  departureTimeString: trip.trip.departureTimeString,
                  arrivalTimeString: trip.trip.arrivalTimeString,
                  operationalInterval: trip.operationalInterval,
                  customWorkingDays: trip.customWorkingDays,
                  hasCustomDates,
                  customDatesType: hasCustomDates ? trip.customWorkingDays![0].type : undefined,
                  prices: trip.prices
                });
              }
            });
          });
          loadPromises.push(loadPromise);
        }
      });

      // Wait for all trip loads to complete
      await Promise.all(loadPromises);

      if (tripIds.length === 0) {
        setIsSubmitting(false);
        return;
      }

      // Navigate to bulk update page with selection state
      const locationState: BulkUpdateLocationState = {
        tripIds,
        tripPreviewRows
      };

      navigate("/trips/bulk-update", { state: locationState });
    } catch (error) {
      console.error("Failed to prepare bulk update", error);
      setIsSubmitting(false);
    }
  }, [selectedLines, navigate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8">
      <div className="bg-white rounded-lg w-full max-w-6xl h-[70vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold">
            Zgjedh linjat dhe oraret për përditësim
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Mbyll"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Line selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zgjedh linjat
            </label>
            <Select
              label=""
              placeholder="Kërko dhe zgjedh linjat..."
              selectType="multiple"
              asyncFunc={getLinesSelectList}
              extraParams={{ operatorId: user?.operator?.id || 0 }}
              selectedItems={selectedLinesArray}
              onSelectedListChange={handleLinesChange}
              getOptionLabel={(line) => line?.label || ""}
              getOptionValue={(line) => line?.value}
              maxHeightList="max-h-60"
            />
          </div>

          {/* Selected lines list */}
          {selectedLines.size > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-700">
                  Linjat e zgjedhura ({selectedLines.size})
                </h3>
                <span className="text-sm text-gray-500">
                  Kliko për të zgjeruar dhe zgjedhur orare specifike
                </span>
              </div>

              <div className="border rounded-lg divide-y">
                {Array.from(selectedLines.entries()).map(([lineId, state]) => (
                  <div key={lineId} className="bg-white">
                    {/* Line row */}
                    <div className="flex items-center p-3 hover:bg-gray-50">
                      <button
                        type="button"
                        onClick={() => toggleLineExpansion(lineId)}
                        className="p-1 hover:bg-gray-200 rounded mr-2"
                      >
                        {state.tripsLoading ? (
                          <SpinnerLoader />
                        ) : state.expanded ? (
                          <ChevronDownIcon className="w-4 h-4" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4" />
                        )}
                      </button>
                      <span className="flex-1 font-medium">{state.line.label}</span>
                      {state.tripsLoaded && (
                        <span className="text-sm text-gray-500 mr-3">
                          {state.trips.length - state.deselectedTripIds.size} / {state.trips.length} orare
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeLine(lineId)}
                        className="p-1 hover:bg-red-100 text-red-600 rounded"
                        aria-label="Largo linjën"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Trips list (expanded) */}
                    {state.expanded && state.tripsLoaded && (
                      <div className="bg-gray-50 px-4 py-2 space-y-1">
                        {state.trips.length === 0 ? (
                          <p className="text-sm text-gray-500 italic py-2">
                            Kjo linjë nuk ka orare.
                          </p>
                        ) : (
                          state.trips.map((trip) => {
                            const isSelected = !state.deselectedTripIds.has(trip.trip.id);
                            return (
                              <label
                                key={trip.trip.id}
                                className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleTripSelection(lineId, trip.trip.id)}
                                  className="mr-3 h-4 w-4 text-primary
                                   rounded border-gray-300 focus:ring-primary"
                                />
                                <span className="text-sm">
                                  {trip.trip.departureTimeString} - {trip.trip.arrivalTimeString}
                                </span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedLines.size === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>Nuk keni zgjedhur asnjë linjë.</p>
              <p className="text-sm mt-1">Përdorni fushën e kërkimit më lart për të zgjedhur linja.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedLines.size > 0 && (
                <span>{selectedLines.size} linja të zgjedhura</span>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                onClick={onClose}
                btnType="custom"
                className="border border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                Anulo
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={selectedLines.size === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <SpinnerLoader />
                    Duke u përgatitur...
                  </span>
                ) : (
                  "Vazhdo"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLineTripPickerModal;
